import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useSnackbar } from 'notistack';
import { CircularProgress, Skeleton } from '@mui/material';
import { IPropsOrder } from '..';
import SecondTypeButton from 'src/components/Items/button/SecondTypeButton';
import Cart from '../Cart';
import CheckoutForm from '../Stripe';
import instance from 'src/sagas/set';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import { scrollToTop } from 'src/common/utils/others';
import { BookingData, IOrderReg } from 'src/models/IOrder';

const stripePromise = loadStripe(process.env.REACT_APP_PUBLISHABLE_KEY!);

const Step3: FC<IPropsOrder & { order: IOrderReg }> = ({ order, onChange, setStep }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { quoteOrder } = useActions();

  const { location, start_date, end_date, end_location, sub_location } = useTypedSelector(
    state => state.van.filters
  );

  const { isLoading, result } = useTypedSelector(state => state.order);

  const {
    extras,
    note,
    van,
    client,
    amount_seats,
    delivery_address,
    return_at,
    isPartial,
    price,
    currency
  } = order;

  const timeOffset = DateTime.fromISO(start_date!).offset;

  const bookingData: BookingData = {
    start_date: start_date!,
    end_date: end_date!,
    location: location!,
    extras,
    note,
    client_id: client.id!,
    van_id: van?.id!,
    host_id: van?.host_id!,
    amount_seats,
    delivery_address,
    return_at,
    isPartial,
    price,
    currency,
    timeOffset,
    end_location: end_location || '',
    sub_location: sub_location || '',
    fee_list: extras
  };
  const handleBack = () => {
    setStep(1);
    scrollToTop();
  };

  const [clientSecret, setClientSecret] = useState('');
  const [orderId, setOrderId] = useState(0);

  useEffect(() => {
    const getClientSecret = async () => {
      const {
        data: { clientSecret, order }
      } = await instance.post(`${process.env.REACT_APP_API}/client/booking`, {
        bookingData
      });
      setClientSecret(clientSecret);
      setOrderId(order.id);
    };
    order.client.id && getClientSecret();
  }, [order.client.id]);

  useEffect(() => {
    result.quote && (navigate('/quote'), enqueueSnackbar(result.quote, { variant: 'success' }));
  }, [result.quote]);

  const options: any = {
    clientSecret,
    appearance: {
      theme: 'stripe'
    }
  };

  return (
    <>
      <div className='order-payment'>
        <div className='col-2 order-payment__container'>
          <div className='order-payment__container__cart-container'>
            <Cart
              order={order}
              titleButton='Reserve van'
              handleBackStep={handleBack}
              onChange={onChange}
            />
          </div>
          <div className='order-payment__price-off'>
            <div className='order-payment__container__payment-container'>
              <p>
                You can book now with a fully refundable down payment applied towards your rental.
              </p>
              <p>
                We'll email a secure link to pay your remainder before your rental. You may pay with
                the same or a different card.
              </p>
              <p>
                You may
                <a
                  href='https://www.sprinter-rentals.com/cancellation'
                  target='_blank'
                  style={{ textDecoration: 'underline' }}
                >
                  cancel
                </a>
                free up to 5 days prior to your trip.
              </p>
            </div>
            <div className='order-payment__payment'>
              {clientSecret ? (
                <Elements options={options} stripe={stripePromise}>
                  <CheckoutForm startDate={bookingData.start_date} buttonName='Pay / Authorize' />
                </Elements>
              ) : (
                <Skeleton sx={{ maxWidth: `100%` }} height={230} style={{ transform: `none` }} />
              )}
              <SecondTypeButton
                children='Back'
                onClick={handleBack}
                className='order-payment__payment__back-button'
              />
            </div>
            <div className='order-payment__quote'>
              <div>
                <h5>Save your quote for later</h5>
                <h6>Van may sell out, price may change</h6>
              </div>
              <SecondTypeButton
                children={isLoading.quote ? <CircularProgress size={15} /> : 'SENT TO MY EMAIL'}
                onClick={() => quoteOrder(orderId)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Step3;
